import { formatGiftDuration } from './giftDuration';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type ExcelJS from 'exceljs';
import { ReservationReport } from '../services/citasService.ts';

export { formatGiftDuration } from './giftDuration';

const BRAND_BLUE: [number, number, number] = [0, 127, 200];
const BRAND_NAVY: [number, number, number] = [15, 23, 42];
const BRAND_PINK: [number, number, number] = [255, 32, 82];
const LIGHT_BLUE = 'EAF6FC';
const BORDER = 'D9E2EC';

const asNumber = (value: string | number | null | undefined) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value: string | number | null | undefined) => `Bs ${asNumber(value).toFixed(2)}`;
const hours = formatGiftDuration;
const valueOrDash = (value?: string | null) => value || '—';
const cleanFilePart = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .toLowerCase();

const reservationFilename = (report: ReservationReport, extension: 'pdf' | 'xlsx') => (
  `reporte_reserva_${cleanFilePart(report.reserva.codigo || String(report.reserva.id))}.${extension}`
);

const dateTime = (value?: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('es-BO');
};

const addPdfTable = (
  document: jsPDF,
  title: string,
  head: string[][],
  body: Array<Array<string | number>>,
  startY: number,
) => {
  const y = startY > 178 ? 24 : startY;
  if (startY > 178) document.addPage();
  document.setFont('helvetica', 'bold');
  document.setFontSize(10);
  document.setTextColor(...BRAND_NAVY);
  document.text(title, 14, y);
  autoTable(document, {
    startY: y + 4,
    head,
    body: body.length ? body : [['Sin registros']],
    theme: 'grid',
    styles: { fontSize: 7.4, cellPadding: 2.2, lineColor: [217, 226, 236], lineWidth: 0.15, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND_BLUE, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 14, right: 14 },
  });
  return ((document as any).lastAutoTable?.finalY || y + 12) + 9;
};

export const createReservationReportPdf = (report: ReservationReport): jsPDF => {
  const document = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const { reserva, finanzas, horas_regalo: gifts, correspondencia } = report;

  document.setFillColor(...BRAND_NAVY);
  document.rect(0, 0, 210, 29, 'F');
  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(17);
  document.text('REPORTE GENERAL DE RESERVA', 14, 12);
  document.setFontSize(9);
  document.setFont('helvetica', 'normal');
  document.text(`Reserva ${reserva.codigo} · Generado ${new Date().toLocaleString('es-BO')}`, 14, 20);
  document.setDrawColor(...BRAND_PINK);
  document.setLineWidth(1.2);
  document.line(14, 26, 196, 26);

  let y = addPdfTable(document, 'INFORMACIÓN DE LA RESERVA', [['Campo', 'Detalle']], [
    ['Código', reserva.codigo],
    ['Estado', reserva.estado],
    ['Estado de ejecución', valueOrDash(reserva.estado_ejecucion)],
    ['Fecha', `${valueOrDash(reserva.fecha_inicio)} — ${valueOrDash(reserva.fecha_fin)}`],
    ['Horario', `${valueOrDash(reserva.hora_inicio)} — ${valueOrDash(reserva.hora_fin)}`],
    ['Servicio', valueOrDash(reserva.servicio)],
    ['Plan', valueOrDash(reserva.plan)],
    ['Espacio', valueOrDash(reserva.espacio)],
    ['Sucursal', valueOrDash(reserva.sucursal)],
  ], 36);

  y = addPdfTable(document, 'RESUMEN FINANCIERO', [['Concepto', 'Importe / estado']], [
    ['Subtotal', money(finanzas.subtotal)],
    ['Descuento', money(finanzas.descuento)],
    ['Total', money(finanzas.total)],
    ['Monto pagado', money(finanzas.pagado)],
    ['Deuda pendiente', money(finanzas.deuda)],
    ['Estado del pago', finanzas.estado_pago],
  ], y);

  y = addPdfTable(document, 'MOVIMIENTOS DE PAGO', [['Código', 'Fecha', 'Tipo', 'Origen', 'Monto']],
    finanzas.pagos.map(payment => [
      valueOrDash(payment.codigo), dateTime(payment.fecha_pago), payment.tipo_pago,
      payment.origen, money(payment.monto),
    ]), y);

  y = addPdfTable(document, 'HORAS DE REGALO', [['Asignadas', 'Consumidas', 'Disponibles']], [[
    hours(gifts.asignadas * 60), hours(gifts.usadas * 60), hours(gifts.disponibles_minutos),
  ]], y);

  y = addPdfTable(document, 'PERÍODOS DE HORAS DE REGALO', [['Desde', 'Hasta', 'Asignadas', 'Usadas', 'Disponibles']],
    gifts.periodos.map(period => [
      period.fecha_inicio, period.fecha_fin, hours(period.asignadas * 60), hours(period.usadas * 60), hours(period.disponibles_minutos),
    ]), y);

  addPdfTable(document, `CORRESPONDENCIA · ${correspondencia.habilitada ? 'Habilitada' : 'No incluida'} (${correspondencia.total})`,
    [['Recepción', 'Descripción', 'Remitente', 'Estado', 'Entrega']],
    correspondencia.items.map(item => [
      dateTime(item.fecha_recepcion), valueOrDash(item.descripcion), valueOrDash(item.remitente),
      item.estado, dateTime(item.fecha_entrega),
    ]), y);

  const pages = document.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    document.setPage(page);
    document.setFontSize(7);
    document.setTextColor(100, 116, 139);
    document.text(`Documento informativo · Página ${page} de ${pages}`, 196, 289, { align: 'right' });
  }

  return document;
};

export const createReservationReportWorkbook = async (report: ReservationReport): Promise<ExcelJS.Workbook> => {
  const { default: ExcelJSRuntime } = await import('exceljs');
  const workbook = new ExcelJSRuntime.Workbook();
  workbook.creator = 'Co Marca';
  workbook.created = new Date();
  const sheet = workbook.addWorksheet('Reporte reserva', {
    views: [{ state: 'frozen', ySplit: 4 }],
    pageSetup: { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
    properties: { defaultRowHeight: 18 },
  });
  sheet.columns = [
    { width: 25 }, { width: 30 }, { width: 23 }, { width: 23 }, { width: 22 }, { width: 22 },
  ];

  const section = (title: string) => {
    const row = sheet.addRow([title]);
    sheet.mergeCells(row.number, 1, row.number, 6);
    row.height = 24;
    row.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF007FC8' } };
      cell.alignment = { vertical: 'middle' };
    });
  };
  const keyValue = (label: string, value: string | number) => {
    const row = sheet.addRow([label, value]);
    row.getCell(1).font = { bold: true, color: { argb: 'FF334155' } };
  };
  const headers = (items: string[]) => {
    const row = sheet.addRow(items);
    row.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${LIGHT_BLUE}` } };
      cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
    });
  };

  const title = sheet.addRow(['REPORTE GENERAL DE RESERVA']);
  sheet.mergeCells('A1:F1');
  title.height = 30;
  title.getCell(1).font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F172A' } };
  title.getCell(1).alignment = { vertical: 'middle' };
  sheet.addRow([`Reserva ${report.reserva.codigo}`, `Generado ${new Date().toLocaleString('es-BO')}`]);
  sheet.addRow([]);

  section('INFORMACIÓN DE LA RESERVA');
  keyValue('Código', report.reserva.codigo);
  keyValue('Estado', report.reserva.estado);
  keyValue('Estado de ejecución', valueOrDash(report.reserva.estado_ejecucion));
  keyValue('Fecha de inicio', valueOrDash(report.reserva.fecha_inicio));
  keyValue('Fecha de fin', valueOrDash(report.reserva.fecha_fin));
  keyValue('Hora de inicio', valueOrDash(report.reserva.hora_inicio));
  keyValue('Hora de fin', valueOrDash(report.reserva.hora_fin));
  keyValue('Servicio', valueOrDash(report.reserva.servicio));
  keyValue('Plan', valueOrDash(report.reserva.plan));
  keyValue('Espacio', valueOrDash(report.reserva.espacio));
  keyValue('Sucursal', valueOrDash(report.reserva.sucursal));
  sheet.addRow([]);

  section('RESUMEN FINANCIERO');
  [
    ['Subtotal', asNumber(report.finanzas.subtotal)],
    ['Descuento', asNumber(report.finanzas.descuento)],
    ['Total', asNumber(report.finanzas.total)],
    ['Monto pagado', asNumber(report.finanzas.pagado)],
    ['Deuda pendiente', asNumber(report.finanzas.deuda)],
  ].forEach(([label, value]) => {
    keyValue(String(label), Number(value));
    sheet.getCell(`B${sheet.rowCount}`).numFmt = '"Bs" #,##0.00';
  });
  keyValue('Estado del pago', report.finanzas.estado_pago);
  sheet.addRow([]);

  section('MOVIMIENTOS DE PAGO');
  headers(['Código', 'Fecha', 'Tipo de pago', 'Origen', 'Monto']);
  report.finanzas.pagos.forEach(payment => {
    const row = sheet.addRow([valueOrDash(payment.codigo), dateTime(payment.fecha_pago), payment.tipo_pago, payment.origen, asNumber(payment.monto)]);
    row.getCell(5).numFmt = '"Bs" #,##0.00';
  });
  if (!report.finanzas.pagos.length) sheet.addRow(['Sin movimientos registrados']);
  sheet.addRow([]);

  section('HORAS DE REGALO');
  keyValue('Asignadas', hours(report.horas_regalo.asignadas * 60));
  keyValue('Consumidas', hours(report.horas_regalo.usadas * 60));
  keyValue('Disponibles', hours(report.horas_regalo.disponibles_minutos));
  headers(['Período desde', 'Período hasta', 'Asignadas', 'Usadas', 'Disponibles']);
  report.horas_regalo.periodos.forEach(period => sheet.addRow([
    period.fecha_inicio, period.fecha_fin, hours(period.asignadas * 60), hours(period.usadas * 60), hours(period.disponibles_minutos),
  ]));
  if (!report.horas_regalo.periodos.length) sheet.addRow(['Sin períodos registrados']);
  sheet.addRow([]);

  section('CORRESPONDENCIA');
  keyValue('Servicio habilitado', report.correspondencia.habilitada ? 'Sí' : 'No');
  keyValue('Total de piezas', report.correspondencia.total);
  headers(['Recepción', 'Descripción', 'Remitente', 'Estado', 'Entrega']);
  report.correspondencia.items.forEach(item => sheet.addRow([
    dateTime(item.fecha_recepcion), valueOrDash(item.descripcion), valueOrDash(item.remitente), item.estado, dateTime(item.fecha_entrega),
  ]));
  if (!report.correspondencia.items.length) sheet.addRow(['Sin correspondencia registrada']);

  sheet.eachRow(row => row.eachCell(cell => {
    cell.alignment = { ...cell.alignment, vertical: 'top', wrapText: true };
  }));
  sheet.pageSetup.margins = { left: 0.25, right: 0.25, top: 0.4, bottom: 0.4, header: 0.15, footer: 0.15 };
  sheet.pageSetup.printArea = `A1:F${sheet.rowCount}`;
  return workbook;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const exportReservationReportPdf = (report: ReservationReport) => {
  createReservationReportPdf(report).save(reservationFilename(report, 'pdf'));
};

export const exportReservationReportExcel = async (report: ReservationReport) => {
  const workbook = await createReservationReportWorkbook(report);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }), reservationFilename(report, 'xlsx'));
};

